import { CommonModule } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnyAbility } from '@casl/ability';
import { AbilityServiceSignal } from '@casl/angular';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faFilter } from '@fortawesome/pro-solid-svg-icons';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { ValueFormatterService } from '@wilson/ag-grid/service';
import { AuthorizationModule } from '@wilson/authorization';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import { FilterKey, FilterModule } from '@wilson/filter';
import {
  RoleAction,
  RolePermissionSubject,
  UserQualificationV2,
} from '@wilson/interfaces';
import { PageFrameV2Component } from '@wilson/page-frame';
import { UserQualificationsGateway } from '@wilson/qualification-v2/common';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDropdownModule } from 'ng-zorro-antd/dropdown';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { QualificationV2ConfigModule } from '../../types';
import { UserQualificationsV2ListService } from '../../service/user-qualifications-v2-list.service';
import { CsvBoxImportQualificationsV2Component } from '../csv-box-import/csv-box-import.component';
import { QualificationsV2ListComponent } from '../qualifications-v2-list/qualifications-v2-list.component';
import { BulkCreateQualificationsDrawerComponent } from './bulk-create-qualifications/drawer/bulk-create-qualifications-drawer.component';
import { BulkCreateQualificationsTriggerComponent } from './bulk-create-qualifications/trigger/bulk-create-qualifications-trigger.component';
import { BulkEditQualificationsDrawerComponent } from './bulk-edit-qualifications/drawer/bulk-edit-qualifications-drawer.component';
import { BulkEditQualificationsTriggerComponent } from './bulk-edit-qualifications/trigger/bulk-edit-qualifications-trigger.component';
import { ComplementaryCertificateExportButtonComponent } from './export/complementary-certificate-export-button.component';
import { QualificationExportDropdownComponent } from './export/qualification-export-dropdown.component';
import { QualificationFilterDrawerComponent } from './filter-drawer/qualification-filter-drawer.component';

interface FilterValues {
  status: string[];
  category: string[];
  orgUnit: string[];
  userIds: string[];
  userLabels: string[];
}
@Component({
  selector: 'wilson-qualification-v2-overview',
  imports: [
    AuthorizationModule,
    BulkCreateQualificationsDrawerComponent,
    BulkCreateQualificationsTriggerComponent,
    BulkEditQualificationsDrawerComponent,
    BulkEditQualificationsTriggerComponent,
    CommonModule,
    ComplementaryCertificateExportButtonComponent,
    CsvBoxImportQualificationsV2Component,
    FeatureFlagsModule,
    FilterModule,
    FontAwesomeModule,
    NzBadgeModule,
    NzButtonModule,
    NzDropdownModule,
    NzTooltipModule,
    PageFrameV2Component,
    QualificationExportDropdownComponent,
    QualificationFilterDrawerComponent,
    QualificationsV2ListComponent,
    TranslatePipe,
  ],
  providers: [ValueFormatterService],
  templateUrl: './qualification-v2-overview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './qualification-v2-overview.component.scss',
})
export class QualificationV2OverviewComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private readonly nzModal = inject(NzModalService);
  private readonly translate = inject(TranslateService);
  private readonly userQualificationsGateway = inject(
    UserQualificationsGateway,
  );
  private readonly userQualificationsV2ListService = inject(
    UserQualificationsV2ListService,
  );
  protected readonly qualificationV2Config =
    inject<QualificationV2ConfigModule>('QUALIFICATION_V2_CONFIG' as any);
  protected readonly abilityService =
    inject<AbilityServiceSignal<AnyAbility>>(AbilityServiceSignal);

  private destroy$ = new Subject<void>();

  faFilter = faFilter;
  isDrawerVisible = false;
  statusFilter = new BehaviorSubject<string[]>([]);
  categoryFilter = new BehaviorSubject<string[]>([]);
  orgUnitFilter = new BehaviorSubject<string[]>([]);
  userIdsFilter = new BehaviorSubject<string[]>([]);
  userLabelsFilter = new BehaviorSubject<string[]>([]);
  searchFilter = new BehaviorSubject<string>('');
  selectedQualifications: UserQualificationV2[] = [];
  public readonly RoleAction = RoleAction;
  public readonly RolePermissionSubject = RolePermissionSubject;
  csvBoxSheetLicenseKey =
    this.qualificationV2Config.csvBoxQualificationV2SheetLicense;
  csvBoxRouteQualificationSheetLicenseKey =
    this.qualificationV2Config.csvBoxRouteQualificationV2SheetLicense;

  protected filterKeys: FilterKey[] = [
    'userQualificationV2Status',
    'qualificationV2Category',
    'orgUnit',
    'userLabels',
    'users',
  ];
  protected numberOfAppliedFilters$!: Observable<number>;
  protected can = this.abilityService.can;
  protected selectionCount$: Observable<{ count: number }> =
    this.userQualificationsV2ListService.selectedQualificationsCount$;

  ngOnInit(): void {
    this.numberOfAppliedFilters$ = this.route.queryParams.pipe(
      map(
        (params) =>
          this.filterKeys.filter((key) => params[key] !== undefined).length,
      ),
    );

    const params = this.route.snapshot.queryParams;
    const {
      userQualificationV2Status,
      qualificationV2Category,
      orgUnit,
      userLabels,
      users,
      search,
    } = params;

    if (userQualificationV2Status) {
      this.statusFilter.next(userQualificationV2Status.split(','));
    }

    if (qualificationV2Category) {
      this.categoryFilter.next(qualificationV2Category.split(','));
    }

    if (orgUnit) {
      this.orgUnitFilter.next(orgUnit.split(','));
    }

    if (userLabels) {
      this.userLabelsFilter.next(userLabels.split(','));
    }

    if (users) {
      this.userIdsFilter.next(users.split(','));
    }

    if (search) {
      this.searchFilter.next(search);
    }

    this.searchFilter.pipe(takeUntil(this.destroy$)).subscribe((search) => {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          search: search || null,
        },
        queryParamsHandling: 'merge',
      });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openDrawer(): void {
    this.isDrawerVisible = true;
  }

  onSearchChange(event: string): void {
    this.searchFilter.next(event);
  }

  onApplyFilters(filters: FilterValues): void {
    this.statusFilter.next(filters.status);
    this.categoryFilter.next(filters.category);
    this.orgUnitFilter.next(filters.orgUnit);
    this.userLabelsFilter.next(filters.userLabels);
    this.userIdsFilter.next(filters.userIds);
  }

  deleteQualifications(): void {
    const selectedQualifications =
      this.userQualificationsV2ListService.getSelectedQualifications();

    this.nzModal.confirm({
      nzTitle: this.translate.instant('page.qualifications.delete_modal.title'),
      nzContent: this.translate.instant(
        'page.qualifications.delete_modal.content',
        {
          qualifications: selectedQualifications.length,
        },
      ),
      nzOkText: this.translate.instant('page.qualifications.delete_modal.ok'),
      nzOkDanger: true,
      nzOnOk: () =>
        this.userQualificationsGateway
          .deleteQualifications(selectedQualifications)
          .pipe(
            tap({
              next: () => {
                this.userQualificationsV2ListService.triggerBulkDeleteQualifications();
              },
            }),
          )
          .subscribe(),
      nzCancelText: this.translate.instant(
        'page.qualifications.delete_modal.cancel',
      ),
    });
  }

  onSelectedRowsChange(rows: UserQualificationV2[]): void {
    this.selectedQualifications = rows;
  }
}
